import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import {
  AltiContext,
  AltiMessage,
  AltiPage,
  AltiSuggestion,
  ExperienceAltiService,
} from "../../services/experience-alti.service";

@Component({
  selector: "app-exp-alti-rail",
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="rail-right">
      <div class="rr-head">
        <div class="rr-brand">
          <span class="dot"></span>
          <div>
            <div class="name">Alti</div>
            <div class="sub">{{ pageLabel }}</div>
          </div>
        </div>
        <button
          class="icon-btn"
          type="button"
          (click)="toggleRequest.emit()"
          title="Collapse (Cmd+\\)"
        >
          <svg viewBox="0 0 24 24"><path d="M13 5l-7 7 7 7" /></svg>
        </button>
      </div>
      <div class="rr-body">
        @if (!messages.length) {
          <div class="insight">
            <div class="insight-head">
              <span class="alti-chip">Alti</span>
              <span class="insight-tag">Briefing</span>
            </div>
            <p>
              I'm following this portfolio's <b>{{ pageLabel.toLowerCase() }}</b
              >. Pick a suggestion or ask me anything.
            </p>
          </div>
        }
        @for (m of messages; track m) {
          <div
            class="rr-msg"
            [class.user]="m.from === 'user'"
            [class.bot]="m.from === 'bot'"
          >
            @if (m.from === "bot") {
              <div class="bot-head">
                <span class="alti-chip">Alti</span>
              </div>
            }
            @if (m.html) {
              <div [innerHTML]="m.html"></div>
            } @else {
              <p>{{ m.text }}</p>
            }
          </div>
        }
        @if (suggestions.length) {
          <div class="rr-section-label">Suggestions</div>
        }
        @if (suggestions.length) {
          <div class="rr-chips">
            @for (s of suggestions; track s) {
              <button class="rr-chip" type="button" (click)="send(s.text)">
                {{ s.text }}
              </button>
            }
          </div>
        }
      </div>
      <div class="rr-input-wrap">
        <input
          class="rr-input"
          type="text"
          [(ngModel)]="draft"
          (keydown.enter)="send(draft)"
          placeholder="Ask Alti..."
          #altiInput
        />
        <button
          type="button"
          class="rr-send"
          (click)="send(draft)"
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  `,
})
export class AltiRailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() page: AltiPage = "optimize";
  @Input() context: AltiContext = { page: "optimize" };
  @Output() toggleRequest = new EventEmitter<void>();

  draft = "";
  messages: AltiMessage[] = [];
  suggestions: AltiSuggestion[] = [];
  pageLabel = "";

  private subs = new Subscription();

  constructor(private alti: ExperienceAltiService) {}

  ngOnInit(): void {
    this.refresh();
    this.subs.add(
      this.alti.ask$.subscribe(({ page, query }) => {
        if (page !== this.page) return;
        this.send(query);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["page"]) {
      this.messages = [];
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private refresh(): void {
    this.suggestions = this.alti.suggestionsFor(this.page);
    const labels: Record<AltiPage, string> = {
      list: "Portfolios",
      edit: "Edit session",
      backtest: "Backtest session",
      optimize: "Optimize session",
      risk: "Risk session",
    };
    this.pageLabel = labels[this.page];
  }

  send(text: string): void {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    this.messages = [...this.messages, { from: "user", text: trimmed }];
    this.draft = "";
    const ctx: AltiContext = { ...this.context, page: this.page };
    setTimeout(() => {
      const reply = this.alti.respond(this.page, trimmed, ctx);
      this.messages = [...this.messages, reply];
    }, 240);
  }
}
